import { FiBook } from 'react-icons/fi';

export const awlSublist6Data = {
  courseId: 'awl-sublist-6',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublist 6',
  icon: FiBook,
  overview: {
    impact: 'Add abstract academic precision so arguments can acknowledge limits and still stay sharp',
    focus: 'accurate, enhance, transform — plus allocate, cite, incentive, utilise',
    uses: 'Reading on research methods; Writing Task 2 when you allocate resources, cite evidence, or propose reform',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word meanings and usage',
      content: `<p>Sublist 6 is where academic English becomes more abstract. You will see these headwords in methods paragraphs, funding debates, and any text that talks about improving a system rather than describing a simple event. Band 7 writers use them to sound exact: not “use” but <em>utilise</em> (when the tool is purposeful), not “make better” but <em>enhance</em>, not “change completely” but <em>transform</em>.</p>
<p>The full Sublist 6 headword set in this course is: abstract, accurate, acknowledge, allocate, assign, author, brief, capable, cite, cooperate, display, diverse, domain, edit, enhance, exceed, expert, explicit, flexible, furthermore, incentive, incorporate, inhibit, initiate, instruct, intelligence, interval, minimum, motive, nevertheless, precede, presume, rational, recover, reveal, scope, transform, transport, underlie, utilise.</p>
<p>Focus first on <strong>accurate</strong>, <strong>enhance</strong>, and <strong>transform</strong>, then add the process verbs <em>allocate</em>, <em>assign</em>, <em>cite</em>, and <em>initiate</em>. Linkers on this list — <em>furthermore</em>, <em>nevertheless</em> — are cohesion tools; learn them as sentence openers, not as content nouns.</p>`,
      points: [
        'These words signal research, planning, and evaluation — common IELTS Reading themes.',
        'Enhance is gradual improvement; transform is a deeper structural change.',
        'Acknowledge is essential for balanced Task 2: you recognise the other side, then argue.',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['abstract', 'Existing as an idea rather than a concrete thing', 'Justice is an abstract concept, but courts must still make concrete decisions.'],
          ['accurate', 'Correct in all details; exact', 'The graph is useful only if the figures are accurate.'],
          ['acknowledge', 'To accept or recognise as true', 'The government acknowledged that the policy had shortcomings.'],
          ['allocate', 'To distribute resources or duties for a purpose', 'The council allocated additional funding to public transport.'],
          ['cite', 'To quote or refer to as evidence', 'The author cited several studies to support the argument.'],
          ['enhance', 'To improve the quality, value, or extent of something', 'Clear examples enhance the main argument.'],
          ['incentive', 'Something that motivates a person to act', 'Tax incentives can encourage firms to reduce emissions.'],
          ['initiate', 'To begin a process or action', 'The university initiated a review of assessment practices.'],
          ['nevertheless', 'In spite of that; however', 'The plan is costly; nevertheless, the long-term savings are clear.'],
          ['scope', 'The range of something that is relevant or possible', 'The scope of the study did not include rural clinics.'],
          ['transform', 'To change completely in form or character', 'Remote work has transformed how many companies allocate office space.'],
          ['utilise', 'To use something effectively for a purpose', 'Schools should utilise existing buildings before they commission new ones.'],
        ],
      },
    },
    {
      title: 'Word families',
      content: `<p>Sublist 6 families are dense. <em>Accurate</em> → <em>accuracy</em> / <em>accurately</em> / <em>inaccurate</em>. <em>Enhance</em> → <em>enhancement</em>. <em>Transform</em> → <em>transformation</em> / <em>transformative</em>. Mixing these is a common Band 6 error: “The enhance was good” or “a transform change.”</p>
<p>Pay attention to British spelling in IELTS materials: <em>utilise</em> / <em>utilisation</em> (US: utilize). Both are understood, but stay consistent. <em>Cite</em> → <em>citation</em> is essential for “according to” paraphrases. <em>Capable</em> → <em>capability</em> / <em>incapable</em> lets you discuss skills without repeating “can” and “cannot.”</p>`,
      points: [
        'After “the” you usually need a noun: accuracy, enhancement, transformation, allocation.',
        'After “is/are” you often need an adjective: accurate, flexible, explicit, rational, diverse.',
        'Underlie / underlying is useful for causes: “the underlying motive.”',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['abstract', 'abstract, abstraction', 'abstract', 'abstract, abstractly'],
          ['accurate', 'accuracy', '—', 'accurate, accurately, inaccurate'],
          ['acknowledge', 'acknowledgement', 'acknowledge', 'acknowledged'],
          ['allocate', 'allocation', 'allocate', 'allocated'],
          ['cite', 'citation', 'cite', 'cited'],
          ['enhance', 'enhancement', 'enhance', 'enhanced'],
          ['incentive', 'incentive', 'incentivise', '—'],
          ['initiate', 'initiation, initiative', 'initiate', 'initial, initially'],
          ['transform', 'transformation', 'transform', 'transformative, transformed'],
          ['utilise', 'utilisation', 'utilise', '—'],
          ['capable', 'capability', '—', 'capable, capably, incapable'],
          ['cooperate', 'cooperation', 'cooperate', 'cooperative, cooperatively'],
        ],
      },
    },
    {
      title: 'Common collocations',
      content: `<p>Collocations keep abstract words grounded. “Allocate resources,” “cite evidence,” “financial incentive,” and “transform a sector” are phrases examiners have seen in good scripts. Isolated “utilise” in every paragraph becomes a tic.</p>
<p>Enhance collocates with performance, understanding, quality, and — in this lesson’s key sentence — an argument. Transform collocates with economy, education, and the way people work. Accurate collocates with data, picture, and prediction. Learn those three sets and you can cover most Task 2 topics on this sublist.</p>`,
      points: [
        'Allocate almost always needs an object: time, funding, resources, staff.',
        'Cite needs a source: a study, a report, a statistic — not “cite that I think.”',
      ],
      examples: [
        { label: 'accurate', text: 'accurate data, an accurate picture, surprisingly accurate' },
        { label: 'acknowledge', text: 'widely acknowledged, acknowledge the fact that, fail to acknowledge' },
        { label: 'allocate', text: 'allocate resources, allocate funding, allocate time, budget allocation' },
        { label: 'cite', text: 'cite a study, cite evidence, frequently cited' },
        { label: 'enhance', text: 'enhance the argument, enhance performance, enhance quality' },
        { label: 'incentive', text: 'financial incentive, strong incentive, incentive to (do)' },
        { label: 'transform', text: 'transform the economy, transformative change, digital transformation' },
        { label: 'utilise', text: 'utilise resources, utilise existing capacity, under-utilised' },
        { label: 'scope', text: 'beyond the scope, limited scope, the scope of the study' },
        { label: 'nevertheless', text: 'costly; nevertheless, … / nevertheless, the benefits outweigh…' },
      ],
    },
    {
      title: 'Using Sublist 6 in IELTS writing',
      content: `<p>A strong body paragraph often does four jobs with this list: <em>acknowledge</em> a limit, <em>cite</em> a reason or example, say what to <em>allocate</em>, and show how the change would <em>enhance</em> or <em>transform</em> an outcome. That is more academic than “some people say… I think we should spend money.”</p>
<p>Use <em>furthermore</em> to add a same-direction point and <em>nevertheless</em> to turn against a concession. Do not open every sentence with them. <em>Explicit</em> instructions and <em>minimum</em> standards are useful in education and law topics. Prefer <em>utilise</em> only when “use” feels too casual <em>and</em> the object is a resource; otherwise “use” is cleaner.</p>`,
      points: [
        'Enhance = improve quality; transform = change the system.',
        'Accurate belongs with data and claims; do not write “an accurate person” when you mean honest.',
        'Motive and incentive are related: motive is why; incentive is the external push.',
      ],
      examples: [
        { label: 'Weak sentence', text: 'Clear examples make the main argument better.' },
        { label: 'Strong sentence', text: 'Clear examples enhance the main argument.' },
        { label: 'Weak sentence', text: 'The city should use money for buses and also change work completely.' },
        { label: 'Strong sentence', text: 'The city should allocate funding to buses; remote work has already begun to transform commuting.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match word to definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        {
          text: 'What does “enhance” mean?',
          options: ['To remove completely', 'To improve the quality or value of something', 'To delay until later'],
          answer: 'To improve the quality or value of something',
          explanation: 'Enhance means make better in quality or extent. It is not a synonym of delete or postpone.',
        },
        {
          text: 'What does “allocate” mean?',
          options: ['To distribute resources for a purpose', 'To cancel a meeting', 'To translate a text'],
          answer: 'To distribute resources for a purpose',
          explanation: 'Allocate is about assigning money, time, or staff. It always implies a limited resource and a target.',
        },
        {
          text: 'What does “acknowledge” mean?',
          options: ['To hide a problem', 'To accept or recognise as true', 'To invent a new theory'],
          answer: 'To accept or recognise as true',
          explanation: 'Acknowledge is the academic verb for admitting a fact or the other side of an argument.',
        },
        {
          text: 'What does “incentive” mean?',
          options: ['A punishment for breaking a rule', 'Something that motivates a person to act', 'A type of graph axis'],
          answer: 'Something that motivates a person to act',
          explanation: 'An incentive pulls behaviour — tax breaks, bonuses, cheaper fares. It is not itself a penalty.',
        },
        {
          text: 'What does “transform” mean?',
          options: ['To change completely in form or character', 'To change a single spelling', 'To stay the same on purpose'],
          answer: 'To change completely in form or character',
          explanation: 'Transform is a deep change, stronger than enhance or edit. Use it when the system itself is different afterwards.',
        },
      ],
    },
    {
      title: 'Complete the sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct Sublist 6 word.',
      questions: [
        {
          text: 'Clear examples ____ the main argument.',
          answer: 'enhance',
          explanation: 'The subject “examples” needs a plural verb. Enhance is the precise academic verb for improving quality.',
        },
        {
          text: 'The council should ____ more of the budget to night buses.',
          answer: 'allocate',
          explanation: 'Allocate collocates with budget, funding, and resources. After “should” use the base verb.',
        },
        {
          text: 'Good essays ____ the opposing view before they reject it.',
          answer: 'acknowledge',
          explanation: 'Acknowledge is the verb for recognising the other side — a Band 7 discussion move.',
        },
        {
          text: 'Digital payments have begun to ____ everyday shopping habits.',
          answer: 'transform',
          explanation: 'Transform fits a wide, structural change in behaviour, not a tiny edit.',
        },
      ],
    },
    {
      title: 'Choose the correct word form',
      type: 'select',
      instructions: 'Select the grammatically correct form for each sentence.',
      questions: [
        {
          text: 'Clear examples ____ the main argument. (enhance)',
          options: ['enhance', 'enhancement', 'enhanced'],
          answer: 'enhance',
          explanation: 'The clause needs a present plural verb after “examples.” Enhancement is a noun; enhanced would need an auxiliary here.',
        },
        {
          text: 'The ____ of the figures is essential before we publish. (accurate)',
          options: ['accurate', 'accuracy', 'accurately'],
          answer: 'accuracy',
          explanation: 'After “the” and before “of” you need the noun. Accurate is an adjective; accurately is an adverb.',
        },
        {
          text: 'Several ____ studies support this claim. (cite)',
          options: ['cite', 'citation', 'cited'],
          answer: 'cited',
          explanation: 'Before “studies” you need an adjective/past participle: cited studies. Cite is a verb; citation is a noun.',
        },
        {
          text: 'Remote work has been a ____ change for many industries. (transform)',
          options: ['transform', 'transformation', 'transformative'],
          answer: 'transformative',
          explanation: 'Before “change” you need an adjective. Transformative describes the kind of change; transformation would need a different structure.',
        },
      ],
    },
  ],
  tips: [
    'Use enhance for quality and transform for systems — they are not interchangeable.',
    'Acknowledge + nevertheless is a clean concession pair in Task 2.',
    'Allocate always needs an object: time, money, staff, or space.',
    'Cite a source or a type of evidence; do not “cite” a personal feeling.',
  ],
  dailyPractice: [
    'Write one sentence that uses allocate and incentive in a tax or education context.',
    'Take a weak sentence with “make better” and rewrite it with enhance or transform.',
    'Read an opinion column and highlight acknowledge, nevertheless, or furthermore.',
    'Test the twelve table words: say the noun form of each verb without looking.',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublist 6 vocabulary course covering Coxhead headwords such as abstract, accurate, acknowledge, allocate, cite, enhance, incentive, initiate, nevertheless, scope, transform, and utilise. Includes the full stub list, word families, collocations, and IELTS writing moves for precision and balanced argument.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist6Data,
      title: awlSublist6Data.title + ' (Foundations)',
      overview: { ...awlSublist6Data.overview, duration: '15-20 minutes' },
      sections: awlSublist6Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist6Data.sections.length / 2))),
      exercises: awlSublist6Data.exercises.slice(0, 1).map((ex) => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist6Data,
      title: awlSublist6Data.title + ' (Advanced)',
      overview: { ...awlSublist6Data.overview, duration: '30-35 minutes' },
      sections: awlSublist6Data.sections,
      exercises: awlSublist6Data.exercises.map((ex) => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist6Data.tips || []),
        'For Band 8+: let underlie name the cause, then allocate name the response.',
        'Advanced tip: use explicit and minimum when you discuss regulation, not only enhance.',
      ],
    };
  }
  return awlSublist6Data;
}
